import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Church, LogOut, LayoutDashboard, CalendarDays, Wallet, HandHeart, Images } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import Overview from "@/components/dashboard/Overview";
import EventsManager from "@/components/dashboard/EventsManager";
import FinanceManager from "@/components/dashboard/FinanceManager";
import PrayerManager from "@/components/dashboard/PrayerManager";
import GalleryManager from "@/components/dashboard/GalleryManager";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [prayers, setPrayers] = useState([]);
  const [gallery, setGallery] = useState([]);

  const reload = useCallback(async () => {
    try {
      const [ev, tx, sm, pr, gl] = await Promise.all([
        api.get("/events"),
        api.get("/transactions"),
        api.get("/finance/summary"),
        api.get("/prayer-requests"),
        api.get("/gallery"),
      ]);
      setEvents(ev.data);
      setTransactions(tx.data);
      setSummary(sm.data);
      setPrayers(pr.data);
      setGallery(gl.data);
    } catch (e) {
      toast.error("No se pudieron cargar los datos");
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-black/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2.5">
            <Church className="h-5 w-5 text-primary" />
            <span className="font-serif text-lg tracking-wide">Cosecha Hashem Adonay</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-muted-foreground sm:inline" data-testid="dash-user-name">
              {user?.name}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-button"
              className="text-muted-foreground hover:text-primary"
            >
              <LogOut className="mr-2 h-4 w-4" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="mb-8">
          <p className="overline text-primary">Panel de Administración</p>
          <h1 className="mt-2 font-serif text-5xl leading-none">Gestión del Ministerio</h1>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="mb-8 h-auto flex-wrap gap-2 bg-muted p-1.5">
            <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <LayoutDashboard className="mr-2 h-4 w-4" /> Resumen
            </TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <CalendarDays className="mr-2 h-4 w-4" /> Eventos
            </TabsTrigger>
            <TabsTrigger value="finance" data-testid="tab-finance" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Wallet className="mr-2 h-4 w-4" /> Finanzas
            </TabsTrigger>
            <TabsTrigger value="prayer" data-testid="tab-prayer" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <HandHeart className="mr-2 h-4 w-4" /> Oración
            </TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Images className="mr-2 h-4 w-4" /> Galería
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <Overview summary={summary} />
          </TabsContent>
          <TabsContent value="events">
            <EventsManager events={events} reload={reload} />
          </TabsContent>
          <TabsContent value="finance">
            <FinanceManager transactions={transactions} events={events} reload={reload} />
          </TabsContent>
          <TabsContent value="prayer">
            <PrayerManager requests={prayers} reload={reload} />
          </TabsContent>
          <TabsContent value="gallery">
            <GalleryManager items={gallery} reload={reload} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
